import { Component, Input } from '@angular/core'
import { IceCorporateAction } from '../models/ice-corporate-action'
import {
  extractIceDetailWithOptions,
  IceDetailWithOptions,
  OptionGroup,
} from '../util/ice-field-extractor'

export interface OptionsMatrix {
  labels: string[]
  groups: OptionGroup[]
}

@Component({
  selector: 'hs-ice-corporate-action-detail',
  templateUrl: './ice-corporate-action-detail.component.html',
  styleUrls: ['./ice-corporate-action-detail.component.scss'],
})
export class IceCorporateActionDetailComponent {
  activeDetailTab = 1
  formattedJson = ''
  detail: IceDetailWithOptions = { mode: 'flat', eventLevelRows: [] }

  private _selectedAction: IceCorporateAction | undefined

  @Input() set selectedAction(value: IceCorporateAction | undefined) {
    this._selectedAction = value
    this.formattedJson = value ? JSON.stringify(value.rawData, null, 2) : ''
    this.detail = value
      ? extractIceDetailWithOptions(value.rawData)
      : { mode: 'flat', eventLevelRows: [] }
  }

  get selectedAction(): IceCorporateAction | undefined {
    return this._selectedAction
  }

  /**
   * Transposes detail.optionGroups (option → list of {property, value}) into
   * the matrix shape needed by the template:
   *   labels — distinct field-labels in first-seen order across options.
   *   groups — the option groups (sort order inherited from the extractor;
   *            see IceDetailWithOptions invariant: ascending by optionNumber).
   *
   * Only meaningful when detail.mode === 'options'. Returns empty matrix otherwise.
   */
  get optionsMatrix(): OptionsMatrix {
    if (this.detail.mode !== 'options' || !this.detail.optionGroups) {
      return { labels: [], groups: [] }
    }
    const groups = this.detail.optionGroups
    const seen = new Set<string>()
    const labels: string[] = []
    for (const group of groups) {
      for (const row of group.rows) {
        if (!seen.has(row.property)) {
          seen.add(row.property)
          labels.push(row.property)
        }
      }
    }
    return { labels, groups }
  }

  /**
   * Returns the value for the given field label within the given option group,
   * or an em-dash ('—') if the option has no row for that label.
   */
  valueFor(label: string, group: OptionGroup): string {
    const row = group.rows.find(r => r.property === label)
    return row ? row.value : '—'
  }
}
